import { useState, useEffect, useMemo } from 'react';

/**
 * Custom hook for preloading images with timeout and error handling
 * Replicates the exact logic used in FarmCardList and SwiperFarmCardList
 * @param {Array} imageUrls - Array of image URLs to preload
 * @param {boolean} shouldPreload - Whether to start preloading
 * @param {number} timeoutMs - Timeout per image in milliseconds (default: 2000)
 * @returns {boolean} preloadComplete - Whether all images have loaded or timed out
 */
export function useImagePreloader(imageUrls = [], shouldPreload = false, timeoutMs = 2000) {
  const [preloadComplete, setPreloadComplete] = useState(false);

  // Memoize the imageUrls array to maintain a stable reference
  // Only updates when the content of the array actually changes
  const memoizedImageUrls = useMemo(() => imageUrls, [JSON.stringify(imageUrls)]);

  useEffect(() => {
    // Reset preload state when new URLs come in or shouldPreload changes
    setPreloadComplete(false);
    
    if (!shouldPreload || memoizedImageUrls.length === 0) return;

    const imagesToPreload = memoizedImageUrls.filter(url => url && typeof url === 'string');

    if (imagesToPreload.length === 0) {
      setPreloadComplete(true);
      return;
    }

    let loaded = 0;
    const totalImages = imagesToPreload.length;
    
    const handleImageComplete = () => {
      loaded++;
      if (loaded === totalImages) {
        setPreloadComplete(true);
      }
    };

    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
      
      // Set timeout to prevent hanging on slow images
      const timeout = setTimeout(() => {
        handleImageComplete();
      }, timeoutMs);
      
      img.onload = () => {
        clearTimeout(timeout);
        handleImageComplete();
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        handleImageComplete(); // Continue even if image fails
      };
    });
  }, [shouldPreload, memoizedImageUrls, timeoutMs]); // Use memoizedImageUrls to detect content changes

  return preloadComplete;
}

/**
 * Custom hook specifically for preloading farm images
 * Replicates the exact logic used in FarmCardList and SwiperFarmCardList
 * @param {Array} farms - Array of farm objects
 * @param {boolean} loading - Loading state
 * @param {number} maxImagesPerFarm - Maximum images to preload per farm (default: 3)
 * @param {number} timeoutMs - Timeout per image in milliseconds (default: 2000)
 * @returns {boolean} preloadComplete - Whether all farm images have loaded or timed out
 */
export function useFarmImagePreloader(farms = [], loading = false, maxImagesPerFarm = 3, timeoutMs = 2000) {
  // Use useMemo to extract image URLs and only recalculate when farms change
  const imageUrls = useMemo(() => {
    const urls = [];
    
    if (!loading && farms.length > 0) {
      farms.forEach(farm => {
        // Collect image URLs from farm data
        if (farm.coverImage?.viewUrl || farm.coverImage?.url) {
          urls.push(farm.coverImage.viewUrl || farm.coverImage.url);
        }
        // Add any other farm images if available
        if (farm.images && farm.images.length > 0) {
          urls.push(...farm.images.slice(0, 2).map(img => img.viewUrl || img.url));
        }
      });
    }
    
    return urls;
  }, [farms, loading]);

  // Use the generic hook with extracted URLs
  return useImagePreloader(imageUrls, !loading && farms.length > 0, timeoutMs);
}
